import { useId } from 'react';
import { TOOL_HISTORY_COPY } from './tool-history-constants';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ToolHistoryEntry } from './ToolHistoryEntry';
import { getToolHistory, toolHistoryKeys } from './tool-history-query';
import styles from './ToolHistory.module.css';

export const ToolHistory = ({ toolId }: { toolId: string }) => {
  const titleId = useId();
  const history = useInfiniteQuery({
    queryKey: toolHistoryKeys.detail(toolId),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => getToolHistory(toolId, pageParam),
    getNextPageParam: page => page.nextCursor ?? undefined,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
  });
  const entries = history.data?.pages.flatMap(page => page.entries) ?? [];

  const olderLabel = history.isFetchingNextPage
    ? TOOL_HISTORY_COPY.loadingMore
    : TOOL_HISTORY_COPY.older;
  const historyIsEmpty = !history.isPending && !history.isError && entries.length === 0;
  const retryHistory = () =>
    history.isFetchNextPageError ? history.fetchNextPage() : history.refetch();

  return (
    <section className={styles.history} aria-labelledby={titleId}>
      <h2 id={titleId}>{TOOL_HISTORY_COPY.title}</h2>
      <p>{TOOL_HISTORY_COPY.description}</p>
      {history.isPending && <p role="status">{TOOL_HISTORY_COPY.loading}</p>}
      {history.isError && (
        <div role="alert">
          {TOOL_HISTORY_COPY.error}{' '}
          <button type="button" onClick={retryHistory}>
            {TOOL_HISTORY_COPY.retry}
          </button>
        </div>
      )}
      {historyIsEmpty && <p>{TOOL_HISTORY_COPY.empty}</p>}
      <ol className={styles.entries}>
        {entries.map(entry => (
          <ToolHistoryEntry key={entry.id} entry={entry} />
        ))}
      </ol>
      {history.hasNextPage && (
        <button
          type="button"
          disabled={history.isFetchingNextPage}
          onClick={() => history.fetchNextPage()}>
          {olderLabel}
        </button>
      )}
    </section>
  );
};
